import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAdminUnitDetail,
  useUnitEvents,
  useOverrideUnit,
  useResetEquipment,
  useResetSimulator,
  useSetAgentToken,
  useUnlockUnit,
  useUnlockLocation,
  useRestoreUnit,
} from '@/api/hooks/useAdminUnits';
import { formatDateTime } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Wrench,
  RotateCcw,
  Power,
  LockOpen,
  Activity,
  Camera,
  Monitor,
  KeyRound,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import type { UnitStatus, UnitOverrideAction } from '@/api/types';

const STATUS_BADGE: Record<UnitStatus, string> = {
  ONLINE: 'bg-green-100 text-green-800',
  OFFLINE: 'bg-red-100 text-red-800',
  DEGRADED: 'bg-yellow-100 text-yellow-800',
  MAINTENANCE: 'bg-blue-100 text-blue-800',
};

const EVENT_COLORS: Record<string, string> = {
  OVERRIDE: 'bg-orange-100 text-orange-800',
  STATUS_CHANGE: 'bg-blue-100 text-blue-800',
  RESET: 'bg-red-100 text-red-800',
  UNLOCK: 'bg-green-100 text-green-800',
};

type ConfirmAction = {
  type: 'override' | 'reset' | 'reset_simulator' | 'unlock_unit' | 'unlock_facility';
  action?: UnitOverrideAction;
  label: string;
};

/** True when the unit has a SIM_PC device — gates the "Reset simulator" button so plug-only bays don't show a dead action. */
function hasSimPc(hardwareConfig?: Record<string, unknown>): boolean {
  const devices = hardwareConfig?.devices;
  return Array.isArray(devices)
    && devices.some((d) => d && typeof d === 'object' && (d as { kind?: unknown }).kind === 'SIM_PC');
}

export default function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [reason, setReason] = useState('');
  const [eventPage, setEventPage] = useState(0);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  // #100 — set-agent-token dialog. The token is write-only: we never receive or display its value, only
  // capture a new plaintext to send. Cleared on close so it never lingers in component state.
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');

  const { data: unit, isLoading } = useAdminUnitDetail(id);
  const { data: eventsData, isLoading: eventsLoading } = useUnitEvents(id ?? '', {
    eventType: eventTypeFilter || undefined,
    page: eventPage,
    size: 15,
  });

  const overrideMutation = useOverrideUnit();
  const resetMutation = useResetEquipment();
  const resetSimulatorMutation = useResetSimulator();
  const setAgentTokenMutation = useSetAgentToken();
  const unlockUnitMutation = useUnlockUnit();
  const unlockLocationMutation = useUnlockLocation();
  const restoreMutation = useRestoreUnit();

  const handleRestore = async () => {
    if (!id) return;
    try {
      await restoreMutation.mutateAsync(id);
      toast.success(t('admin.unit_restored_toast'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const closeTokenDialog = () => {
    setTokenDialogOpen(false);
    setTokenInput('');
  };

  const handleSetAgentToken = async () => {
    if (!id || !tokenInput.trim()) return;
    try {
      await setAgentTokenMutation.mutateAsync({ unitId: id, agentToken: tokenInput.trim() });
      toast.success(t('admin.agent_token_set_success'));
      closeTokenDialog();
    } catch (err: unknown) {
      const appErr = err as { message?: string };
      toast.error(appErr.message || t('common.error'));
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction || !id) return;
    try {
      if (confirmAction.type === 'override' && confirmAction.action) {
        await overrideMutation.mutateAsync({
          unitId: id,
          action: confirmAction.action,
          reason: reason || confirmAction.label,
        });
      } else if (confirmAction.type === 'reset') {
        await resetMutation.mutateAsync({ unitId: id, reason });
      } else if (confirmAction.type === 'reset_simulator') {
        // A human who has been warned may reset mid-session — send force when a session is active.
        const res = await resetSimulatorMutation.mutateAsync({
          unitId: id,
          reason,
          force: !!unit?.currentSession,
        });
        // Report the agent's real outcome honestly, not a generic "done".
        if (res.result === 'READY') {
          toast.success(t('admin.reset_simulator_ready'));
        } else if (res.result === 'DEGRADED') {
          toast.warning(t('admin.reset_simulator_degraded', { detail: res.detail ?? '' }));
        } else {
          toast.error(t('admin.reset_simulator_failed', { detail: res.detail ?? '' }));
        }
        setConfirmAction(null);
        setReason('');
        return;
      } else if (confirmAction.type === 'unlock_unit') {
        await unlockUnitMutation.mutateAsync({ unitId: id, reason });
      } else if (confirmAction.type === 'unlock_facility' && unit) {
        await unlockLocationMutation.mutateAsync({ locationId: unit.locationId, reason });
      }
      toast.success(t('admin.action_success'));
    } catch (err: unknown) {
      const appErr = err as { status?: number; code?: string; message?: string };
      if (appErr.status === 409) {
        toast.error(t('admin.reset_blocked_active'));
      } else {
        toast.error(appErr.message || t('common.error'));
      }
    }
    setConfirmAction(null);
    setReason('');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('admin.unit_not_found')}
      </div>
    );
  }

  const statusClass = STATUS_BADGE[unit.status] ?? STATUS_BADGE.ONLINE;

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/units')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{unit.name}</h1>
            <Badge variant="secondary" className={`text-xs ${statusClass}`}>
              {t(`admin.${unit.status.toLowerCase()}`)}
            </Badge>
            {/* #93 — agent reachability, only when this unit has a SIM_PC device (absent = no badge, not a red one). */}
            {unit.agentHealth && (
              <Badge
                variant="secondary"
                className={`text-xs ${unit.agentHealth.reachable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
              >
                {unit.agentHealth.reachable
                  ? t('admin.agent_reachable', { version: unit.agentHealth.agentVersion ?? '' })
                  : t('admin.agent_unreachable')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t(`sport_${unit.sportCode}`, { defaultValue: unit.sportName })} · {unit.locationName}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <Card>
        <CardContent className="flex flex-wrap gap-2 p-4">
          {/* #88 — restore, only for a genuinely Layer-4-suspended unit (not manually OFFLINE) */}
          {unit.suspended && (
            <Button
              size="sm"
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              <Power className="mr-1.5 h-4 w-4" />
              {t('admin.unit_restore')}
            </Button>
          )}
          {unit.status === 'MAINTENANCE' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setConfirmAction({ type: 'override', action: 'CLEAR_MAINTENANCE', label: t('admin.clear_maintenance') })
              }
            >
              <Wrench className="mr-1.5 h-4 w-4" />
              {t('admin.clear_maintenance')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setConfirmAction({ type: 'override', action: 'SET_MAINTENANCE', label: t('admin.set_maintenance') })
              }
            >
              <Wrench className="mr-1.5 h-4 w-4" />
              {t('admin.set_maintenance')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmAction({ type: 'reset', label: t('admin.reset') })}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            {t('admin.reset')}
          </Button>
          {/* #93 — "Reset simulator" (prepare on the sim PC), only when the unit has a SIM_PC device.
               Kept DISTINCT from the power-cycle above: different blast radius, different recovery time. */}
          {hasSimPc(unit.hardwareConfig) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction({ type: 'reset_simulator', label: t('admin.reset_simulator') })}
            >
              <Monitor className="mr-1.5 h-4 w-4" />
              {t('admin.reset_simulator')}
            </Button>
          )}
          {/* Unlock buttons — context-aware based on access model */}
          {(unit.accessModel === 'FACILITY_ONLY' || unit.accessModel === 'FACILITY_AND_UNIT') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction({ type: 'unlock_facility', label: t('admin.unlock_facility') })}
            >
              <LockOpen className="mr-1.5 h-4 w-4" />
              {t('admin.unlock_facility')}
            </Button>
          )}
          {(unit.accessModel === 'UNIT_ONLY' || unit.accessModel === 'FACILITY_AND_UNIT') &&
            !!unit.hardwareConfig?.smartLockId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction({ type: 'unlock_unit', label: t('admin.unlock_bay') })}
            >
              <LockOpen className="mr-1.5 h-4 w-4" />
              {t('admin.unlock_bay')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* #100 — Agent token, only for a unit that HAS a SIM_PC device (an action on a unit that can't use it
           teaches operators the UI lies — same gate as "Reset simulator"). WRITE-ONLY: we show whether a
           token is set, never its value; the field is masked and the backend never returns the token. */}
      {hasSimPc(unit.hardwareConfig) && (
        <Card>
          <CardContent className="p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('admin.agent_token')}
            </h4>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                {unit.agentTokenSet ? (
                  <>
                    <span className="font-mono tracking-widest text-muted-foreground">••••••••••••</span>
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                      {t('admin.agent_token_set')}
                    </Badge>
                  </>
                ) : (
                  <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-700">
                    {t('admin.agent_token_not_set')}
                  </Badge>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setTokenDialogOpen(true)}>
                <KeyRound className="mr-1.5 h-4 w-4" />
                {t('admin.agent_token_set_action')}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t('admin.agent_token_hint')}</p>
          </CardContent>
        </Card>
      )}

      {/* Camera Feed */}
      <Card>
        <CardContent className="p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('admin.camera_feed')}
          </h4>
          {unit.hardwareConfig?.cameraUrl ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <img
                src={unit.hardwareConfig.cameraUrl as string}
                alt={`${unit.name} camera`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-video rounded-lg bg-muted flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('admin.no_camera_configured')}</p>
                <p className="text-xs mt-1">{t('admin.camera_setup_hint')}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="status">
        <TabsList>
          <TabsTrigger value="status">{t('admin.tab_status')}</TabsTrigger>
          <TabsTrigger value="events">{t('admin.tab_events')}</TabsTrigger>
        </TabsList>

        {/* Status tab */}
        <TabsContent value="status">
          <div className="space-y-4">
            {/* Active session */}
            {unit.currentSession ? (
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                    <h3 className="text-sm font-semibold">{t('admin.active_session')}</h3>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Activity className="h-4 w-4 text-green-600" />
                    <span className="font-medium">{unit.currentSession.customerName}</span>
                    <span className="text-muted-foreground">
                      — {t('admin.ends_at')} {new Date(unit.currentSession.endTime).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4 text-sm text-muted-foreground">
                  {t('admin.no_active_session')}
                </CardContent>
              </Card>
            )}

            {/* Features */}
            {unit.features && Object.keys(unit.features).length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <h3 className="mb-2 text-sm font-semibold">{t('admin.features')}</h3>
                  <div className="grid gap-1 text-sm">
                    {Object.entries(unit.features).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span>{Array.isArray(value) ? (value as string[]).join(', ') : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Event log tab */}
        <TabsContent value="events">
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <select
                  value={eventTypeFilter}
                  onChange={(e) => { setEventTypeFilter(e.target.value); setEventPage(0); }}
                  className="rounded-md border bg-transparent px-2 py-1 text-sm"
                >
                  <option value="">{t('admin.all_event_types')}</option>
                  <option value="OVERRIDE">Override</option>
                  <option value="STATUS_CHANGE">Status Change</option>
                  <option value="RESET">Reset</option>
                  <option value="UNLOCK">Unlock</option>
                </select>
              </div>

              {eventsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              ) : eventsData && eventsData.events.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[100px]">{t('admin.col_type')}</TableHead>
                        <TableHead className="min-w-[120px]">{t('admin.col_action')}</TableHead>
                        <TableHead>{t('admin.col_result')}</TableHead>
                        <TableHead className="hidden md:table-cell">{t('admin.col_reason')}</TableHead>
                        <TableHead className="min-w-[140px]">{t('admin.col_time')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventsData.events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <Badge variant="secondary" className={`text-xs ${EVENT_COLORS[event.eventType] ?? ''}`}>
                              {event.eventType}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{event.action}</TableCell>
                          <TableCell className="text-sm">
                            {event.result ? (
                              <Badge
                                variant="secondary"
                                className={`text-xs ${
                                  event.result.includes('BLOCKED') || event.result === 'FAILED'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {event.result}
                              </Badge>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground truncate max-w-[200px]">
                            {event.reason || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(event.createdAt, i18n.language)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {eventsData.total > 15 && (
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {t('admin.page_of', { page: eventPage + 1, total: Math.ceil(eventsData.total / 15) })}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={eventPage === 0}
                          onClick={() => setEventPage((p) => p - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={(eventPage + 1) * 15 >= eventsData.total}
                          onClick={() => setEventPage((p) => p + 1)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t('admin.no_events')}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) { setConfirmAction(null); setReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmAction?.label}</DialogTitle>
          </DialogHeader>
          {/* #93 — a human may reset the simulator mid-session, but must be warned first (A6b: automation never does). */}
          {confirmAction?.type === 'reset_simulator' && unit?.currentSession && (
            <p className="rounded-md bg-yellow-50 p-2 text-sm text-yellow-800">
              {t('admin.reset_simulator_active_warning')}
            </p>
          )}
          <div>
            <Label>{t('admin.reason')}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.reason_placeholder')}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmAction(null); setReason(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={overrideMutation.isPending || resetMutation.isPending || resetSimulatorMutation.isPending || unlockUnitMutation.isPending || unlockLocationMutation.isPending}
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* #100 — set agent token. The value is never pre-filled (write-only); we only capture a new plaintext. */}
      <Dialog open={tokenDialogOpen} onOpenChange={(open) => { if (!open) closeTokenDialog(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.agent_token_set_action')}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>{t('admin.agent_token')}</Label>
            <Input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder={t('admin.agent_token_placeholder')}
              className="mt-1 font-mono"
              autoComplete="off"
            />
            <p className="mt-2 text-xs text-muted-foreground">{t('admin.agent_token_hint')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeTokenDialog}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSetAgentToken}
              disabled={!tokenInput.trim() || setAgentTokenMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
